import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useStore } from '@thd-nucleus/experience-context';
import {
  Input,
  FormController,
  FormLabel,
  FormHelpMessage,
  Link,
  Popover,
  Typography
} from '@one-thd/sui-atomic-components';
import {
  giftCardPinImgUrl,
  storeLocatorUrl,
  VERBIAGE,
  containerWidthClass
} from '../../utils/consts';
import { isNumber } from '../../utils/utils';

const PinPopover = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const prevOpen = React.useRef(open);
  const { storeId } = useStore();

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <div className="sui-w-full" data-testid="pin-popover-wrapper">
      <Link
        href="#basic-link"
        color="subtle"
        underline="always"
        onClick={handleToggle}
        ref={anchorRef}
        variant="body-xs"
        data-testid="pin-popover-trigger"
      >
        {VERBIAGE.pinTitle}
      </Link>
      <Popover
        variant="body-xs"
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        title={VERBIAGE.pinTitle}
        style={{ zIndex: 1371 }}
      >
        <img
          src={giftCardPinImgUrl}
          alt="Back of Gift Card"
          border="0"
          className={`sui-relative ${containerWidthClass} sui-h-48`}
          width={384}
          height={192}
        />
        <Typography variant="body-xs" color="primary">
          Please visit one of our U.S. stores if your card does not have a PIN.
          <Link
            underline="always"
            variant="body-xs"
            href={storeLocatorUrl}
            target="_blank"
            data-testid="pin-popover-find-store"
          >
            Find your local store
          </Link>
        </Typography>
      </Popover>
    </div>
  );
};

const CardPinInput = ({ onReady, disabled }) => {
  const [pin, setPin] = useState('');
  const [status, setStatus] = useState(null);

  const handleChange = (event) => {
    const { value } = event?.target;
    if (value && !isNumber(value)) {
      return;
    }
    setPin(value);
    if (value.length === 4) {
      setStatus(null);
      onReady(value);
    }
  };

  const handleOnBlur = (event) => {
    const { value } = event?.target;
    if (!value) {
      return;
    }

    if (value.length < 4) {
      setStatus('error');
      onReady('');
    } else {
      setStatus(null);
      onReady(value);
    }
  };

  const renderHelpMessage = () => {
    if (!status) {
      return null;
    }

    return (
      <FormHelpMessage
        id="input-card-number-message"
        className="sui-text-danger"
        data-testid="input-card-number-message"
      >
        Please provide the 4-digit gift card PIN.
      </FormHelpMessage>
    );
  };

  return (
    <FormController fullWidth>
      <FormLabel required htmlFor="pin-input">
        PIN
      </FormLabel>
      <Input
        disabled={disabled}
        value={pin}
        onBlur={handleOnBlur}
        onChange={handleChange}
        status={status}
        aria-labelledby="input-pin"
        data-testid="input-pin"
        placeholder="0000"
        inputAttributes={{
          maxLength: 4
        }}
      />
      {renderHelpMessage()}
      <PinPopover />
    </FormController>
  );
};

CardPinInput.propTypes = {
  onReady: PropTypes.func,
  disabled: PropTypes.bool
};

CardPinInput.defaultProps = {
  onReady: () => {},
  disabled: false
};

export default CardPinInput;
